<template>
  <div class="eval-sample-drilldown" v-loading="loading">
    <template v-if="sample">
      <div class="query-header">
        <div class="query-text">{{ sample.query_text }}</div>
        <div class="query-meta">
          <el-tag v-if="sample.query_category" size="small" type="info">{{ sample.query_category }}</el-tag>
          <span>期望数量: {{ sample.target_count }}</span>
          <span>sample_key: {{ sample.sample_key }}</span>
        </div>
      </div>

      <!-- 粗推 LLM 调用（单发会话 pipeline_mode='single' 没有粗推阶段，整块不渲染） -->
      <template v-if="!isSingleMode">
        <el-collapse v-model="coarseActive" class="stage-collapse">
          <el-collapse-item v-if="coarseCall" name="coarse">
            <template #title>
              <span class="collapse-title">粗推 LLM 调用</span>
              <span class="collapse-summary">
                {{ coarseCall.request_model }} · {{ formatParams(coarseCall.request_params) }} ·
                耗时 {{ fmtNum(coarseCall.elapsed_s, 1) }}s · 请求 {{ sample.coarse_requested }} 首 ·
                原始行 {{ sample.coarse_raw_lines }} · 格式错误 {{ sample.coarse_malformed }} ·
                重复 {{ sample.coarse_duplicates }}
              </span>
            </template>
            <el-alert
              v-if="coarseCall.error"
              type="error"
              :closable="false"
              :title="`调用失败: ${coarseCall.error}`"
              style="margin-bottom: 10px;"
            />
            <template v-else>
              <div v-for="(msg, idx) in coarseCall.request_messages" :key="idx" class="msg-block">
                <div class="msg-role">{{ roleLabel(msg.role) }}</div>
                <pre class="msg-content">{{ msg.content }}</pre>
              </div>
              <div class="msg-block">
                <div class="msg-role">响应内容</div>
                <pre class="msg-content">{{ coarseCall.response_content || '(空)' }}</pre>
              </div>
              <div class="usage-line">Token 用量: {{ formatUsage(coarseCall.response_usage) }}</div>
            </template>
          </el-collapse-item>
        </el-collapse>
        <el-empty v-if="!coarseCall" description="无粗推调用记录" :image-size="50" />
      </template>

      <!-- 候选池匹配结果（single 会话没有粗推/候选池阶段，整块不渲染，对齐粗推区块处理方式） -->
      <template v-if="!isSingleMode">
        <h4 class="section-title">候选池匹配结果（{{ verdicts.length }} 条）</h4>
        <el-table v-if="verdicts.length > 0" :data="verdicts" stripe size="small" style="width: 100%; margin-bottom: 20px;">
          <el-table-column label="歌名" prop="song" show-overflow-tooltip />
          <el-table-column label="歌手" prop="artist" show-overflow-tooltip width="140" />
          <el-table-column label="裁决" width="100">
            <template #default="scope">
              <el-tag :type="verdictTagType(scope.row.verdict)" size="small">{{ verdictLabel(scope.row.verdict) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="匹配类型" width="90">
            <template #default="scope">{{ scope.row.match_type || '-' }}</template>
          </el-table-column>
          <el-table-column label="匹配结果" show-overflow-tooltip>
            <template #default="scope">
              <span v-if="scope.row.matched_title">{{ scope.row.matched_title }} - {{ scope.row.matched_artist }}</span>
              <span v-else>-</span>
            </template>
          </el-table-column>
        </el-table>
        <el-empty v-else description="无候选池数据（粗推可能失败）" :image-size="50" style="margin-bottom: 20px;" />
      </template>

      <!-- 精排 LLM 调用（单发会话复用 stage='rerank' 承载，文案改「单发调用」，见设计 2.2 节） -->
      <el-collapse v-model="rerankActive" class="stage-collapse">
        <el-collapse-item v-if="rerankCall" name="rerank">
          <template #title>
            <span class="collapse-title">{{ isSingleMode ? '单发调用' : '精排 LLM 调用' }}</span>
            <span class="collapse-summary">
              {{ rerankCall.request_model }} · {{ formatParams(rerankCall.request_params) }} ·
              耗时 {{ fmtNum(rerankCall.elapsed_s, 1) }}s · 越界丢弃 {{ sample.rerank_invalid_index }} ·
              格式错误 {{ sample.rerank_malformed_lines }} · 重复引用 {{ sample.rerank_duplicates }}
            </span>
          </template>
          <el-alert
            v-if="rerankCall.error"
            type="error"
            :closable="false"
            :title="`调用失败: ${rerankCall.error}`"
            style="margin-bottom: 10px;"
          />
          <template v-else>
            <div v-for="(msg, idx) in rerankCall.request_messages" :key="idx" class="msg-block">
              <div class="msg-role">{{ roleLabel(msg.role) }}</div>
              <pre class="msg-content">{{ msg.content }}</pre>
            </div>
            <div class="msg-block">
              <div class="msg-role">响应内容</div>
              <pre class="msg-content">{{ rerankCall.response_content || '(空)' }}</pre>
            </div>
            <el-collapse v-model="thinkingActive" class="thinking-collapse">
              <el-collapse-item title="思考内容（reasoning_content）" name="thinking">
                <pre v-if="rerankCall.response_reasoning_content" class="msg-content">{{ rerankCall.response_reasoning_content }}</pre>
                <span v-else class="empty-hint">无思考内容</span>
              </el-collapse-item>
            </el-collapse>
            <div class="usage-line">Token 用量: {{ formatUsage(rerankCall.response_usage) }}</div>
          </template>
        </el-collapse-item>
      </el-collapse>
      <el-empty
        v-if="!rerankCall"
        :description="isSingleMode ? '该样本无单发调用记录' : '该样本未执行精排（粗推失败或候选池为空）'"
        :image-size="50"
      />

      <!-- 精排成品 -->
      <template v-if="sample.rerank_selections && sample.rerank_selections.length > 0">
        <h4 class="section-title">精排成品</h4>
        <div class="rerank-product">
          <div class="rerank-title">{{ sample.rerank_title || '(无标题)' }}</div>
          <div v-if="sample.rerank_overall_reason" class="rerank-reason">{{ sample.rerank_overall_reason }}</div>
          <ol class="selection-list">
            <li v-for="(sel, idx) in sample.rerank_selections" :key="idx">
              <span class="selection-track">{{ resolveSelectionLabel(sel) }}</span>
              <span v-if="sel.reason" class="selection-reason"> — {{ sel.reason }}</span>
            </li>
          </ol>
        </div>
      </template>

      <!-- 结构化指标 -->
      <h4 class="section-title">结构化指标</h4>
      <div class="metrics-cards">
        <el-card v-if="metrics.hallucination" shadow="never" class="metric-card">
          <template #header>幻觉 / 网络异常</template>
          <div>幻觉率: {{ fmtPct(metrics.hallucination.hallucination_rate) }}</div>
          <div>网络异常率: {{ fmtPct(metrics.hallucination.network_error_rate) }}</div>
          <div class="muted">
            not_found={{ metrics.hallucination.not_found }} ·
            all_rejected={{ metrics.hallucination.all_rejected }} ·
            network_error={{ metrics.hallucination.network_error }}
          </div>
        </el-card>
        <el-card v-if="metrics.survival" shadow="never" class="metric-card">
          <template #header>存活 / 坍缩</template>
          <div>存活率: {{ fmtPct(metrics.survival.survival_vs_requested) }}</div>
          <div>池/目标比: {{ fmtNum(metrics.survival.pool_vs_target) }}</div>
          <div>
            <el-tag v-if="metrics.survival.collapse_flag" type="danger" size="small">坍缩</el-tag>
            <span v-else class="muted">未坍缩</span>
          </div>
        </el-card>
        <el-card v-if="metrics.fuzzy" shadow="never" class="metric-card">
          <template #header>模糊匹配</template>
          <div>模糊匹配率: {{ fmtPct(metrics.fuzzy.fuzzy_rate) }}</div>
          <div class="muted">模糊/匹配: {{ metrics.fuzzy.fuzzy_count }} / {{ metrics.fuzzy.matched_count }}</div>
        </el-card>
        <el-card v-if="metrics.rerank" shadow="never" class="metric-card">
          <template #header>精排结构</template>
          <div>
            数量: {{ metrics.rerank.count }} / 目标 {{ metrics.rerank.target_count }}
            <el-tag v-if="metrics.rerank.count_exact" type="success" size="small">精确</el-tag>
          </div>
          <div>
            <el-tag v-if="metrics.rerank.is_degraded" type="warning" size="small">
              降级（原始 {{ metrics.rerank.raw_selection_count }} 首截断）
            </el-tag>
            <span v-else class="muted">未降级</span>
          </div>
          <div class="muted">
            顺序惰性ρ: {{ fmtNum(metrics.rerank.order_spearman_vs_pool) }} ·
            重复引用: {{ metrics.rerank.duplicates }}
          </div>
        </el-card>
      </div>
      <el-empty v-if="Object.keys(metrics).length === 0" description="暂无结构化指标" :image-size="50" />
    </template>
    <el-empty v-else-if="!loading" description="样本不存在" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import axios from 'axios';
import { ElMessage } from 'element-plus';

const props = defineProps({
  runId: { type: String, required: true },
  sampleKey: { type: String, required: true },
  // run 级字段，随「下钻」跳转由 EvalRunDetail/EvalDashboard 透传（'three_stage' | 'single'）
  pipelineMode: { type: String, default: 'three_stage' },
});

const sample = ref(null);
const loading = ref(false);
const isSingleMode = computed(() => props.pipelineMode === 'single');

// 逐字 messages/reasoning 默认折叠，避免大文本渲染卡顿
const coarseActive = ref([]);
const rerankActive = ref([]);
const thinkingActive = ref([]);

const fmtPct = (v) => (v === null || v === undefined ? '-' : `${(v * 100).toFixed(1)}%`);
const fmtNum = (v, digits = 3) => (v === null || v === undefined ? '-' : Number(v).toFixed(digits));

const roleLabelMap = { system: '系统提示', user: '用户输入', assistant: '助手' };
const roleLabel = (role) => roleLabelMap[role] || role;

const formatParams = (params) => {
  if (!params || Object.keys(params).length === 0) return '-';
  return Object.entries(params).map(([k, v]) => `${k}=${v}`).join(', ');
};

const formatUsage = (usage) => {
  if (!usage || Object.keys(usage).length === 0) return '-';
  const { prompt_tokens: p, completion_tokens: c, total_tokens: t } = usage;
  return `输入 ${p ?? '-'} / 输出 ${c ?? '-'} / 合计 ${t ?? '-'}`;
};

const verdictLabelMap = { matched: '匹配', not_found: '未找到', all_rejected: '全部拒绝', network_error: '网络错误' };
const verdictTagMap = { matched: 'success', not_found: 'info', all_rejected: 'danger', network_error: 'warning' };
const verdictLabel = (v) => verdictLabelMap[v] || v;
const verdictTagType = (v) => verdictTagMap[v] || '';

const coarseCall = computed(() => sample.value?.llm_calls?.find((c) => c.stage === 'coarse'));
const rerankCall = computed(() => sample.value?.llm_calls?.find((c) => c.stage === 'rerank'));
const verdicts = computed(() => sample.value?.filter_verdicts || []);
const matchedVerdicts = computed(() => verdicts.value.filter((v) => v.verdict === 'matched'));
const metrics = computed(() => sample.value?.structural_metrics || {});

const resolveSelectionLabel = (sel) => {
  const track = matchedVerdicts.value[sel.index - 1];
  if (!track) {
    return `#${sel.index}（无法解析，索引越界或数据缺失）`;
  }
  return `${track.matched_title} - ${track.matched_artist}`;
};

const loadSample = async () => {
  if (!props.runId || !props.sampleKey) return;
  loading.value = true;
  sample.value = null;
  try {
    const resp = await axios.get(`/api/eval/runs/${props.runId}/samples/${props.sampleKey}`);
    if (resp.data.code === 1) {
      sample.value = resp.data.data;
    } else {
      ElMessage.error(resp.data.msg || '加载样本详情失败');
    }
  } catch (err) {
    console.error('加载样本详情失败:', err);
    if (err.response && err.response.status !== 404) {
      ElMessage.error(err.response?.data?.msg || '加载样本详情失败');
    }
  } finally {
    loading.value = false;
  }
};

watch(() => [props.runId, props.sampleKey], loadSample);
onMounted(loadSample);
</script>

<style scoped>
.query-header {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #ebeef5;
}

.query-text {
  font-size: 17px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 6px;
}

.query-meta {
  display: flex;
  gap: 16px;
  align-items: center;
  color: #909399;
  font-size: 13px;
}

.stage-collapse {
  margin-bottom: 16px;
}

.collapse-title {
  font-weight: bold;
  margin-right: 12px;
}

.collapse-summary {
  color: #909399;
  font-size: 12px;
}

.msg-block {
  margin-bottom: 12px;
}

.msg-role {
  font-weight: bold;
  color: #606266;
  font-size: 13px;
  margin-bottom: 4px;
}

.msg-content {
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 10px;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: monospace;
  font-size: 13px;
  max-height: 400px;
  overflow-y: auto;
  margin: 0;
}

.usage-line {
  color: #909399;
  font-size: 13px;
  margin-top: 8px;
}

.thinking-collapse {
  margin: 10px 0;
}

.empty-hint {
  color: #c0c4cc;
}

.section-title {
  margin: 20px 0 12px;
  color: #303133;
}

.rerank-product {
  background: #f9fafc;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 20px;
}

.rerank-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.rerank-reason {
  color: #909399;
  margin-top: 4px;
  margin-bottom: 10px;
}

.selection-list {
  margin: 0;
  padding-left: 20px;
}

.selection-list li {
  padding: 4px 0;
}

.selection-track {
  font-weight: bold;
  color: #303133;
}

.selection-reason {
  color: #606266;
}

.metrics-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
}

.metric-card :deep(.el-card__header) {
  padding: 8px 15px;
  font-weight: bold;
  font-size: 13px;
}

.metric-card :deep(.el-card__body) {
  padding: 12px 15px;
  font-size: 13px;
  line-height: 1.8;
}

.muted {
  color: #909399;
}
</style>
